'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useI18n } from '@/lib/i18n'
import { gameQueries } from '@/lib/queries'
import { Loader2, Plus, ShieldCheck, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

export function WhitelistSection() {
  const { t } = useI18n()
  const [emails, setEmails] = useState<string[]>([])
  const [newEmail, setNewEmail] = useState('')
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<string | null>(null)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  const loadEmails = useCallback(async () => {
    setLoading(true)
    try {
      const result = await gameQueries.getWhitelistEmails()
      if (result.success && result.data)
        setEmails(result.data)
    }
    catch (error) {
      console.error('Failed to load whitelist emails:', error)
      setMessage({ type: 'error', text: 'Failed to load whitelist' })
    }
    finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadEmails()
  }, [loadEmails])

  const handleAdd = async () => {
    const trimmed = newEmail.trim()
    if (!trimmed)
      return
    setAdding(true)
    setMessage(null)
    try {
      await gameQueries.addWhitelistEmail(trimmed)
      setNewEmail('')
      await loadEmails()
      setMessage({ type: 'success', text: `"${trimmed}" added to whitelist` })
    }
    catch (error) {
      console.error('Failed to add email:', error)
      setMessage({ type: 'error', text: 'Failed to add email. Make sure the format is valid.' })
    }
    finally {
      setAdding(false)
    }
  }

  const handleRemove = async (email: string) => {
    setRemoving(email)
    setMessage(null)
    try {
      await gameQueries.removeWhitelistEmail(email)
      await loadEmails()
      setMessage({ type: 'success', text: `"${email}" removed from whitelist` })
    }
    catch (error) {
      console.error('Failed to remove email:', error)
      setMessage({ type: 'error', text: 'Failed to remove email' })
    }
    finally {
      setRemoving(null)
    }
  }

  return (
    <div className="bg-slate-50 px-4 py-8 dark:bg-slate-900 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl">
        <div className="mb-8 text-center">
          <h2 className="mb-2 text-xl font-bold text-foreground md:text-2xl lg:text-3xl">
            {t('admin.nav.whitelist', 'Email Whitelist')}
          </h2>
          <p className="text-sm text-muted-foreground">
            Whitelisted emails get full access to all game modes without a purchase. They are treated the same as paid customers.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="size-5" />
              Whitelisted Emails ({emails.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {message && (
              <p
                className={`text-sm ${message.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-destructive'}`}
              >
                {message.text}
              </p>
            )}

            {/* Add email input */}
            <div className="flex flex-wrap gap-2">
              <Input
                placeholder="user@example.com"
                type="email"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
                className="max-w-[300px]"
              />
              <Button onClick={handleAdd} disabled={adding || !newEmail.trim()} size="sm" className="gap-1">
                {adding ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
                Add
              </Button>
            </div>

            {/* Email list */}
            {loading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                Loading...
              </div>
            ) : emails.length > 0 ? (
              <ul className="flex flex-wrap gap-2">
                {emails.map(email => (
                  <li
                    key={email}
                    className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/50 px-2 py-1 text-sm"
                  >
                    <span>{email}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-5 shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => handleRemove(email)}
                      disabled={removing === email}
                      title="Remove"
                    >
                      {removing === email ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Trash2 className="size-4" />
                      )}
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No whitelisted emails yet.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
